import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Grid,
  Typography,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';
import DOMPurify from 'dompurify';
 
interface Insights {
  insightId: number;
  title: string;
  description: string;
  opsType: string;
  createdBy: string | null;
  updatedBy: string | null;
  createdDate: string;
  updatedDate: string;
  technology?: string | null;
  month?: string;
}
 
interface InsightAccordionItemProps {
  insight: Insights;
  expanded: boolean;
  showForm: boolean;
  onToggle: () => void;
  onDelete: (id: number) => void;
  onEdit: (insight: Insights) => void;
}
 
const formatCSTTime = (isoDate: string) => {
  const date = new Date(isoDate);
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'America/Chicago',
  };
 
  return `Created: ${date.toLocaleString('en-US', options)} CST`;
};
 
const InsightAccordionItem: React.FC<InsightAccordionItemProps> = ({
  insight,
  expanded,
  showForm,
  onToggle,
  onDelete,
  onEdit,
}) => {
  const theme = useTheme();
 
  const formatDescription = (description: string) => {
    if (!description) return '';
    const formatted = description.replace(
      /<img /g,
      '<img style="width: 100%; max-width: 100%; height: auto; display: block; margin-top: 8px;" ',
    );
    return DOMPurify.sanitize(formatted);
  };
 
  return (
    <Grid
      container
      sx={{
        pb: 1,
        marginBottom: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Accordion
        expanded={expanded}
        onChange={() => onToggle()}
        disableGutters
        elevation={0}
        square
        sx={{
          width: '100%',
          backgroundColor: 'transparent !important',
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
            backgroundColor: 'transparent !important',
          },
          '& .MuiAccordionSummary-root.Mui-expanded': {
            backgroundColor: 'transparent !important',
          },
          '&&.MuiAccordion-root .MuiAccordionDetails-root': {
            backgroundColor: 'transparent !important',
            border: 'none !important',
            borderTop: 'none !important',
            boxShadow: 'none !important',
            outline: 'none !important',
          },
        }}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon
              sx={{
                color: `${theme.palette.textLight.secondary} !important`,
              }}
            />
          }
          sx={{
            px: 1,
            py: 1,
            minHeight: expanded ? 'auto' : '44px',
            backgroundColor: expanded ? 'transparent !important' : theme.palette.accordion.bg,
            marginBottom: expanded ? 1 : 0,
            '&.Mui-expanded': {
              minHeight: 'auto',
              backgroundColor: 'transparent !important',
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
              alignItems: 'center',
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: 0,
            },
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography fontSize="18px" fontWeight={500} color={theme.palette.textLight.main}>
              {insight.title}
            </Typography>
 
            <Typography
              variant="body2"
              fontStyle="italic"
              fontSize={12}
              color={theme.palette.textLight.main}
              fontWeight={300}
              sx={{ mr: 1 }}
            >
              {formatCSTTime(insight.updatedDate)}
            </Typography>
          </Grid>
        </AccordionSummary>
 
        <AccordionDetails
          sx={{
            p: 1,
            backgroundColor: 'transparent !important',
          }}
        >
          <Box
            sx={{
              width: '100%',
              backgroundColor: 'transparent !important',
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                backgroundColor: 'transparent',
              },
              '& th, & td': {
                px: '8px',
                color: `${theme.palette.textLight.main}`,
                backgroundColor: 'transparent',
                textAlign: 'left',
              },
              '& th': {
                fontWeight: 'bold',
              },
              marginBottom: 2,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            <div
              dangerouslySetInnerHTML={{ __html: formatDescription(insight.description) }}
              style={{
                width: '100%',
                color: theme.palette.textLight.main,
                backgroundColor: 'transparent',
              }}
            />
          </Box>
 
          <Box
            sx={{
              marginTop: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
              backgroundColor: 'transparent !important',
            }}
          >
            <Button
              sx={{ color: theme.palette.error.main, fontWeight: '700' }}
              onClick={() => onDelete(insight.insightId)}
            >
              DELETE
            </Button>
 
            {!showForm && (
              <Button
                sx={{ color: `${theme.palette.textLight.secondary}`, fontWeight: '700' }}
                onClick={() => onEdit(insight)}
              >
                UPDATE
              </Button>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};
 
export default InsightAccordionItem;